import { prepareEstimatePdfImages } from '@/lib/sales/prepare-pdf-line-images';
import type { PaymentSettings } from '@/types/settings';
import type {
  AttachmentImage,
  PdfCompanyInfo,
  PdfCustomFieldValue,
} from '@/components/documents/estimates/pdfTypes';
import type { Estimate } from '@/types/estimate';
import type { EstimateDocLayout } from '@/types/estimateDocTemplate';
import { canUseStoredEstimatePdf } from '@/lib/sales/estimate-rendering';
import { deriveServiceFeeDisclosure } from '@/lib/estimate/serviceFee';
import type { PublicPaymentPlanPreview } from '@/components/public/PaymentPlanPreview';

function triggerDownload(blob: Blob, fileName: string) {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(blobUrl);
}

export async function downloadEstimatePdf(
  id: string,
  fileName: string,
  timeZone?: string | null,
): Promise<void> {
  const [
    { apiGet, apiPost, operationsClient },
    {
      opsEstimateEndpoints,
      settingsEndpoints,
      customFieldEndpoints,
      paymentScheduleTemplateEndpoints,
    },
    { queryClient },
    { estimateKeys, settingsKeys },
    { formatCustomFieldValueForPdf },
  ] = await Promise.all([
    import('@/lib/api'),
    import('@/lib/api/endpoints'),
    import('@/lib/queryClient'),
    import('@/lib/queryKeys'),
    import('@/components/organisms/custom-fields/custom-field-form-utils'),
  ]);

  // NOT-238: fetch fresh (staleTime 0). A stale cached estimate from before
  // approval has no activePdf, so the signed-PDF short-circuit below would miss
  // and we'd regenerate an UNSIGNED copy that overwrites the signed activePdf.
  const estimate = await queryClient.fetchQuery<Record<string, unknown>>({
    queryKey: estimateKeys.detail(id),
    queryFn: async () => {
      const res = await apiGet<Record<string, unknown>>(
        operationsClient,
        opsEstimateEndpoints.detail(id),
      );
      // Carry `wisetack` (the financing disclosure, a COMPLIANCE block) and
      // `depositCollectedSummary` (the fee a card deposit actually charged) onto
      // the estimate so the downloaded PDF renders them exactly like the public
      // / signed document — both are siblings of `estimate` on the response.
      return {
        ...(res.estimate as Record<string, unknown>),
        activePdf: res.activePdf,
        wisetack: res.wisetack ?? null,
        depositCollectedSummary: res.depositCollectedSummary ?? null,
      };
    },
    staleTime: 0,
  });

  const status = estimate.status as string | undefined;
  const isApproved = status === 'APPROVED';
  const activePdf = estimate.activePdf as
    | { url?: string; reason?: string | null }
    | null
    | undefined;
  // If an active PDF exists in S3, download it directly. For APPROVED estimates
  // this is the canonical signed copy the customer received.
  if (activePdf?.url && canUseStoredEstimatePdf(estimate as unknown as Estimate, activePdf)) {
    const { url } = await apiGet<{ url: string }>(
      operationsClient,
      opsEstimateEndpoints.pdfDownloadUrl(id),
    );
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to fetch PDF');
    const blob = await res.blob();
    triggerDownload(blob, fileName);
    return;
  }

  // Fetch everything needed for fresh generation (all parallel)
  const [attachmentImages, cfValues, companySettings, paymentPlan, paymentSettings] =
    await Promise.all([
      apiGet<AttachmentImage[]>(operationsClient, opsEstimateEndpoints.attachmentImages(id)).catch(
        () => [] as AttachmentImage[],
      ),
      apiGet<
        {
          fieldDefinitionId: string;
          label: string;
          value: unknown;
          isCustomerFacing: boolean;
          fieldType: string;
          config: Record<string, unknown>;
        }[]
      >(operationsClient, customFieldEndpoints.values(), {
        params: { entityType: 'estimate', entityId: id },
      }).catch(() => []),
      queryClient
        .fetchQuery<PdfCompanyInfo>({
          // NOT-236: this is the PDF path, so request the inline logo data URI
          // (react-pdf can't fetch the presigned logoUrl cross-origin). Distinct
          // query key from the on-screen company settings so the eager-fetch
          // cost stays scoped to PDF generation.
          queryKey: settingsKeys.companyWithLogoDataUri(),
          queryFn: () =>
            apiGet<PdfCompanyInfo>(operationsClient, settingsEndpoints.company(), {
              params: { includeLogoDataUri: true },
            }),
          staleTime: 60_000,
        })
        .catch(() => null as PdfCompanyInfo | null),
      apiGet<PublicPaymentPlanPreview | null>(
        operationsClient,
        paymentScheduleTemplateEndpoints.preview(id),
      ).catch(() => null),
      // Needed for the document-level service-fee note, so a downloaded PDF
      // matches the on-screen preview. Fail-soft: deriveServiceFeeDisclosure
      // fails closed on null settings, and a settings hiccup must never block
      // a download.
      queryClient
        .fetchQuery<PaymentSettings>({
          queryKey: settingsKeys.payments(),
          queryFn: () =>
            apiGet<PaymentSettings>(operationsClient, settingsEndpoints.paymentSettings()),
          staleTime: 60_000,
        })
        .catch(() => null as PaymentSettings | null),
    ]);

  const customFields: PdfCustomFieldValue[] = cfValues
    .filter((v) => v.isCustomerFacing && v.value != null)
    .map((v) => ({
      fieldDefinitionId: v.fieldDefinitionId,
      label: v.label,
      value: formatCustomFieldValueForPdf(v.fieldType, v.value, v.config ?? {}),
    }));

  // Mirrors EstimatePDFPreview so a downloaded PDF carries the same
  // document-level service-fee note the rep sees on screen, with the exact fee
  // the capture payment recorded (the response-root summary carried above).
  const serviceFee = deriveServiceFeeDisclosure(paymentSettings, {
    collected: estimate.depositCollected as boolean | null | undefined,
    method: estimate.depositPaymentMethod as string | null | undefined,
    chargedAmount: (
      estimate.depositCollectedSummary as { serviceFeeAmount?: number } | null | undefined
    )?.serviceFeeAmount,
  });

  // The block layout this estimate renders through is resolved by the SERVER
  // (same contract as the customer-facing document: snapshot → chosen template →
  // tenant default → stock), so a downloaded PDF never diverges from what the
  // customer receives. Fail-soft: any hiccup falls back to the stock default
  // design rather than blocking the download.
  const resolvedDocLayout: EstimateDocLayout | null = await apiGet<{
    layout: EstimateDocLayout | null;
  }>(operationsClient, opsEstimateEndpoints.documentLayout(id))
    .then((r) => r?.layout ?? null)
    .catch(() => null);

  const [{ EstimateDocumentPdf }, { pdf }] = await Promise.all([
    import('@/components/documents/estimates/EstimateDocumentPdf'),
    import('@react-pdf/renderer'),
  ]);

  const pdfEstimate = await prepareEstimatePdfImages(estimate as unknown as Estimate, {
    audience: 'staff',
  });
  const blob = await pdf(
    <EstimateDocumentPdf
      estimate={pdfEstimate}
      company={companySettings}
      attachmentImages={attachmentImages}
      customFields={customFields}
      timeZone={timeZone}
      paymentPlan={paymentPlan}
      serviceFee={serviceFee}
      wisetack={
        (estimate as { wisetack?: import('@/types/public').PublicWisetackCta | null }).wisetack ??
        null
      }
      resolvedDocLayout={resolvedDocLayout}
    />,
  ).toBlob();

  triggerDownload(blob, fileName);

  // NOT-238: never let a client-side regen overwrite an APPROVED estimate's
  // canonical signed PDF with an unsigned one. (Reaching here for an APPROVED
  // estimate means its signed activePdf was momentarily unavailable; the user
  // still gets a downloaded copy, but we must not clobber the stored signed
  // artifact.)
  if (isApproved) return;

  // Persist to S3 + DB (non-fatal)
  try {
    const { uploadUrl, fileUrl } = await apiPost<{ uploadUrl: string; fileUrl: string }>(
      operationsClient,
      opsEstimateEndpoints.pdfUploadUrl(id),
    );
    await fetch(uploadUrl, {
      method: 'PUT',
      body: blob,
      headers: { 'Content-Type': 'application/pdf' },
    });
    await apiPost(operationsClient, opsEstimateEndpoints.confirmPdf(id), {
      url: fileUrl,
      reason: 'generated',
    });
    void queryClient.invalidateQueries({ queryKey: estimateKeys.detail(id) });
  } catch {
    // Non-fatal — PDF already downloaded
  }
}
